import { toQuery, nodeWithArgs, node, inlineFragment, enumeration } from 'common/util/queryHelper';
import { queryGraphQL } from 'common/util/query';
import { addApplicationMessages } from 'common/actions/applicationMessagesActions';
import { goBack } from 'react-router-redux';
import { get, head, isEmpty } from 'lodash';

export const ACTIONS = {
    WORKFLOW_LOAD_DOCUMENT: 'WORKFLOW_LOAD_DOCUMENT',
    WORKFLOW_UNLOAD_DOCUMENT: 'WORKFLOW_UNLOAD_DOCUMENT'
};

export function loadDocumentViewerByTask(taskId, shouldGoBack = true) {
    return dispatch => {
        const query = nodeWithArgs('taskDetails', {
            taskId: taskId
        }, [
            inlineFragment('SDFATask', [
                node('document', [
                    'id',
                    'name',
                    'conversionStatus',
                    nodeWithArgs('renditions', {
                        type: enumeration('VIEW'),
                    }, [
                        'objectName',
                        'containerId',
                        'prettyFileName',
                        'fileExt',
                        'mimeType',
                        'fileSize',
                        'filePurpose',
                        'createdDate',
                        node('downloadRequest', [
                            'tempUrl'
                        ])
                    ]),
                ])
            ])
        ]);

        return queryGraphQL(toQuery(query)).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(error => error.message)));
                if (shouldGoBack)
                    dispatch(goBack());
            } else {
                const document = get(response.data, 'taskDetails.document', null);
                if (document) {
                    dispatch(writeWorkflowViewer(document));
                } else {
                    dispatch(addApplicationMessages('error', 'No document is associated with this task.'));
                    if (shouldGoBack)
                        dispatch(goBack());
                }
            }
        });
    };
}

export function loadDocumentViewerByWorkflow(workflowId, dateRange, shouldGoBack = false) {
    dateRange = dateRange ? {
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString()
    } : {};
    const query = toQuery(nodeWithArgs('myWorkflows', {
        id: workflowId
    }, [
        'id',
        node('document', [
            'id',
            'name',
            'documentStatus',
            'conversionStatus',
            node('uploadUser', [
                'sub',
                'displayName',
                'emailAddress'
            ]),
            node('commitUser', [
                'sub',
                'displayName',
                'emailAddress'
            ]),
            'commitDate',
            'uploadDate',
            node('descriptors', [
                'id',
                'dataType',
                'displayName',
                'values',
                'parent'
            ]),
            node('documentType', [
                'id',
                'displayName'
            ]),
            nodeWithArgs('renditions', {
                type: enumeration('VIEW'),
            }, [
                'objectName',
                'containerId',
                'prettyFileName',
                'fileExt',
                'mimeType',
                'fileSize',
                'filePurpose',
                'createdDate',
                node('downloadRequest', [
                    'tempUrl'
                ])
            ]),
            shouldGoBack && nodeWithArgs('history', {
                dateRange
            }, [
                'date',
                node('actions', [
                    'action',
                    'timestamp',
                    node('data', [
                        'type',
                        'value'
                    ]),
                    node('user', [
                        'sub',
                        'name',
                        'emailAddress'
                    ])
                ])
            ]),
            node('signatures', [
                node('user', ['sub', 'name', 'emailAddress']),
                node('statement', ['id', 'name', 'statement']),
                'dateSigned'
            ]),
        ])
    ]));

    return dispatch => {
        return queryGraphQL(query).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(error => error.message)));
                if (shouldGoBack)
                    dispatch(goBack());
            } else {
                const workflow = head(get(response.data, 'myWorkflows', {}));
                const document = get(workflow, 'document', {});

                if (!isEmpty(document)) {
                    dispatch(writeWorkflowViewer(document));
                } else {
                    dispatch(addApplicationMessages('error', ['No document is associated with this workflow.']));
                    if (shouldGoBack)
                        dispatch(goBack());
                }
            }
        });
    };
}

export function loadDocumentHistoryByWorkflow(workflowId, docId, dateRange) {
    const query = toQuery(nodeWithArgs('myWorkflows', {
        id: workflowId
    }, [
        'id',
        node('document', [
            'id',
            nodeWithArgs('history', {
                dateRange: {
                    start: dateRange.start.toISOString(),
                    end: dateRange.end.toISOString()
                }
            }, [
                'date',
                node('actions', [
                    'action',
                    'timestamp',
                    node('data', [
                        'type',
                        'value'
                    ]),
                    node('user', [
                        'sub',
                        'name',
                        'emailAddress'
                    ])
                ])
            ]),
        ])
    ]));

    return (dispatch, getState) => {
        return queryGraphQL(query).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(error => error.message)));
                dispatch(goBack());
            } else {
                const workflow = head(get(response.data, 'myWorkflows', {}));
                const newDocument = get(workflow, 'document', {});
                const previousDocument = get(getState(), 'workflowViewer.document');
                const document = { ...previousDocument, ...newDocument };

                dispatch(writeWorkflowViewer(document));
            }
        });
    };
}

export function unloadWorkflowViewer() {
    return {
        type: ACTIONS.WORKFLOW_UNLOAD_DOCUMENT
    };
}

function writeWorkflowViewer(document) {
    return {
        type: ACTIONS.WORKFLOW_LOAD_DOCUMENT,
        payload: document
    };
}
